import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError, subscribeToAuthState } from "../api/client";

type Theme = "light" | "dark";

function getSavedTheme(): Theme {
  try {
    return localStorage.getItem("natalify-theme") === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [authenticated, setAuthenticated] = useState(() => api.isAuthenticated());

  useEffect(() => subscribeToAuthState(() => setAuthenticated(api.isAuthenticated())), []);

  if (!authenticated) return <NatalifyLockScreen />;
  return <>{children}</>;
}

function NatalifyLockScreen() {
  const [pin, setPin] = useState("");
  const [now, setNow] = useState(() => new Date());
  const [theme, setTheme] = useState<Theme>(getSavedTheme);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("natalify-theme", theme);
    } catch {
      // Theme preference is optional.
    }
  }, [theme]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const addDigit = (digit: string) => {
    setPin((current) => current + digit);
    setError(null);
  };

  const removeDigit = () => {
    setPin((current) => current.slice(0, -1));
    setError(null);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!pin || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.login(pin);
      setPin("");
    } catch (loginError) {
      setPin("");
      setError(loginError instanceof ApiError ? loginError.message : "Could not sign in. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const time = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  const date = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <main className="natalify-lockscreen">
      <div className="lockscreen-content">
        <header className="lockscreen-brand">
          <div>
            <p className="brand-mark">NATALIFY</p>
            <p className="brand-subtitle">Packing desk</p>
          </div>
          <button
            className="icon-button theme-toggle"
            type="button"
            onClick={() => setTheme((current) => current === "light" ? "dark" : "light")}
            aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
          >
            {theme === "light" ? "☾" : "☀"}
          </button>
        </header>

        <section className="lockscreen-clock" aria-label="Current time">
          <p className="lockscreen-date">{date}</p>
          <p className="lockscreen-time">{time}</p>
        </section>

        <section className="lockscreen-access">
          <form onSubmit={submit}>
            <label className="sr-only" htmlFor="natalify-passcode">Passcode</label>
            <input
              id="natalify-passcode"
              className="lockscreen-passcode"
              type="password"
              inputMode="none"
              autoComplete="off"
              readOnly
              value={pin}
              onKeyDown={(event) => {
                if (/^\d$/.test(event.key)) {
                  event.preventDefault();
                  addDigit(event.key);
                } else if (event.key === "Backspace") {
                  event.preventDefault();
                  removeDigit();
                }
              }}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "natalify-auth-error" : undefined}
            />

            <div className="lockscreen-keypad" role="group" aria-label="Number keypad">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "delete", "0", "submit"].map((key) => key === "submit" ? (
                <button
                  className="lockscreen-key lockscreen-submit-key"
                  type="submit"
                  key={key}
                  aria-label={submitting ? "Checking passcode" : "Unlock"}
                  aria-busy={submitting}
                  disabled={!pin || submitting}
                >
                  <img className="lockscreen-submit-logo" src="/brand/logo.jpeg" alt="" aria-hidden="true" />
                  {submitting ? <span className="lockscreen-spinner" aria-hidden="true" /> : <span className="lockscreen-submit-arrow" aria-hidden="true">→</span>}
                </button>
              ) : key === "delete" ? (
                <button className="lockscreen-key lockscreen-delete" type="button" key={key} onClick={removeDigit} aria-label="Delete last number" disabled={!pin || submitting}>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5H20a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H9l-6-7 6-7Z" /><path d="m12 9 6 6m0-6-6 6" /></svg>
                </button>
              ) : (
                <button className="lockscreen-key" type="button" key={key} onClick={() => addDigit(key)} disabled={submitting}>{key}</button>
              ))}
            </div>

            {error && <p className="lockscreen-error" id="natalify-auth-error" role="alert">{error}</p>}
          </form>
        </section>
      </div>
    </main>
  );
}
